import { MeterOwnerType } from '@enmon/commons/lib/enmon/meters';
import { Commodity } from '@enmon/commons/lib/shared';
import { InventoryMeterMountType } from '@enmon/commons/lib/tools/api/inventory-meter';
import type { OutputTechnology } from '@enmon/commons/lib/tools/types';
import { getDefaultBuildingFloorOptions } from '@/features/inventorization/building-floor-options';
import { getDetailEnumOptions } from '@/features/inventorization/detail-field-options';
import type { InventorizationContext } from '@/features/inventorization/domain/inventorization-context';
import type { InventorizationIdentity } from '@/features/inventorization/domain/inventorization-identity';
import {
  formatInventorizationNewMeterSelection,
  readCommodity,
  readInventorizationNewMeterSelection,
  readMeterPlacementSelection,
} from '@/features/inventorization/domain/inventorization-identity';
import type { InventorizationPosition } from '@/features/inventorization/domain/inventorization-position';
import type {
  InventorizationMainReading,
  InventorizationRegisterReading,
} from '@/features/inventorization/domain/inventorization-reading';
import type { InventorizationTechnicalDetails } from '@/features/inventorization/domain/inventorization-technical';
import {
  mergeInbuiltOutputs,
  readMountType,
  readOutputTechnology,
} from '@/features/inventorization/domain/inventorization-technical';
import {
  INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS,
  INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS,
  INVENTORIZE_TECHNICAL_DETAIL_REVIEW_FIELD_IDS,
  INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS,
  type InventorizeRegisterReviewFieldId,
  readInventorizeRegisterReviewFieldId,
  readInventorizeTechnicalDetailReviewFieldKey,
} from '@/features/inventorization/domain/review/inventorize-review-field-ids';
import type { InventorizeReviewField } from '@/features/inventorization/domain/review/inventorize-review-fields';
import {
  catalogField,
  nativeSelectField,
  optionField,
  rssiField,
  type SaveResult,
  textField,
} from '@/features/inventorization/field-wizard-definition-builders';
import {
  detailWizardDescription,
  detailWizardLabel,
  detailWizardPlaceholder,
  fieldWizardText,
} from '@/features/inventorization/field-wizard-i18n';
import {
  getInventorizationCommodityOptions,
  getInventorizationNewMeterOptions,
  getInventorizationPlacementOptions,
} from '@/features/inventorization/identity-options';
import type { InventoryBuildingOption } from '@/features/inventorization/new-meter-location-client';
import { OUTSIDE_BUILDING_SELECT_VALUE } from '@/features/inventorization/placement-options';
import { outputTechnologyOptions, remoteReadingMountTypeOptions } from '@/features/inventorization/technical-options';
import type { FieldWizardDefinition } from '@/routers/inventorization/components/GenericFieldEditor';
import { VALUE_SCAN_PURPOSES } from '@/shared/camera/value-scanner';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import {
  getObisOptionsForCommodity,
  getTariffOptions,
  getUnitOptionsForCommodity,
} from '@/shared/meters/register-metadata';
import { OCR_CANDIDATE_FIELDS } from '@/shared/ocr/ocr-candidates';

type KeysOfUnion<T> = T extends T ? keyof T : never;
type CommonsMeterDetailKey = Extract<
  KeysOfUnion<NonNullable<import('@enmon/commons/lib/enmon/meters').UpdateMeterDetails['detail']>>,
  string
>;

const BOOLEAN_DETAIL_KEYS = [
  'isDirect',
  'flange',
  'hotWater',
  'woltman',
] as const satisfies readonly CommonsMeterDetailKey[];

const WIZARD_NUMBER_DETAIL_KEYS = [
  'layLength',
  'pMax',
  'qMax',
  'qMin',
  'width',
] as const satisfies readonly CommonsMeterDetailKey[];
const WIDTH_DETAIL_KEY = 'width';
const WIDTH_VALUE_DETAIL_KEY = 'width.value';
const WIDTH_UNIT_DETAIL_KEY = 'width.unit';
const WIDTH_UNIT_DIN = 'DIN';
const WIDTH_UNIT_MM = 'mm';
const OWNER_TYPE_DETAIL_KEY = 'ownerType';
type WidthUnit = typeof WIDTH_UNIT_DIN | typeof WIDTH_UNIT_MM;

export const INVENTORIZATION_BOOLEAN_DETAIL_SELECTIONS = {
  false: 'false',
  true: 'true',
  unspecified: '',
} as const;

export interface FieldWizardDefinitionInput {
  buildingOptions: InventoryBuildingOption[];
  context: InventorizationContext;
  field: InventorizeReviewField;
  identity: InventorizationIdentity;
  manufacturerOptions: Array<{ label: string; value: string }>;
  meterModels: Array<{ id: string; inbuiltOutputs: OutputTechnology[]; name: string }>;
  modelOptions: Array<{ label: string; value: string }>;
  onSaveContext: (patch: Partial<InventorizationContext>) => SaveResult;
  onSaveIdentity: (patch: Partial<InventorizationIdentity>) => SaveResult;
  onSavePosition: (patch: Partial<InventorizationPosition>) => SaveResult;
  onSaveReading: (patch: Partial<InventorizationMainReading>) => SaveResult;
  onSaveRegisterReading: (registerId: string, patch: Partial<InventorizationRegisterReading>) => SaveResult;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => SaveResult;
  ownerType: string;
  ownerSuggestions: Array<{ label: string; value: string }>;
  position: InventorizationPosition;
  reading: InventorizationMainReading;
  registers: InventorizationRegisterReading[];
  technical: InventorizationTechnicalDetails;
  tenantOptions: Array<{ label: string; value: string }>;
  utilityCompanyOwnerOptions: Array<{ label: string; value: string }>;
}

export function createFieldWizardDefinition(input: FieldWizardDefinitionInput): FieldWizardDefinition {
  const registerField = readInventorizeRegisterReviewFieldId(input.field.id);

  if (registerField) {
    return createRegisterFieldDefinition({
      fieldId: registerField,
      identity: input.identity,
      onSaveRegisterReading: input.onSaveRegisterReading,
      registers: input.registers,
    });
  }

  const contextField = createContextFieldDefinition(input);

  if (contextField) {
    return contextField;
  }

  const identityField = createIdentityFieldDefinition(input);

  if (identityField) {
    return identityField;
  }

  const readingField = createReadingFieldDefinition(input);

  if (readingField) {
    return readingField;
  }

  switch (input.field.id) {
    case 'position.gps':
      return textField(
        input.position.gpsUnavailableReason,
        fieldWizardText(
          'position.gps',
          'description',
          'Record why GPS could not be captured, or leave empty when GPS is available.',
        ),
        fieldWizardText('position.gps', 'placeholder', 'GPS unavailable reason'),
        value => input.onSavePosition({ gpsUnavailableReason: value }),
      );
    case 'position.floorplan':
      return {
        currentValue:
          input.position.floorplanLat !== null && input.position.floorplanLng !== null
            ? `${input.position.floorplanLng}, ${input.position.floorplanLat}`
            : input.position.floorplanNotPlacedReason,
        description: fieldWizardText(
          'position.floorplan',
          'description',
          'Place the meter on the floorplan or record why visual placement is skipped.',
        ),
        placeholder: fieldWizardText('position.floorplan', 'placeholder', 'Open the floorplan placement wizard.'),
        save: () => undefined,
        type: 'readonly',
      };
    default:
      return createTechnicalFieldDefinition(input);
  }
}

function createContextFieldDefinition(input: FieldWizardDefinitionInput): FieldWizardDefinition | null {
  switch (input.field.id) {
    case INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.locationName:
      return textField(
        input.context.locationName,
        fieldWizardText('context.locationName', 'description', 'Confirm the inventory location name.'),
        fieldWizardText('context.locationName', 'placeholder', 'Location'),
        value => input.onSaveContext({ locationName: value }),
      );
    case INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.buildingName:
      return nativeSelectField(
        input.context.buildingName || input.position.building,
        fieldWizardText('context.buildingName', 'description', 'Confirm the building for this meter.'),
        fieldWizardText('context.buildingName', 'placeholder', 'Select building'),
        [
          {
            label: translateInventoryText(
              'app.inventory.position.building.outsideNotSpecified',
              'Outside / not specified',
            ),
            value: OUTSIDE_BUILDING_SELECT_VALUE,
          },
          ...input.buildingOptions.map(building => ({ label: building.name, value: building.name })),
        ],
        async value => {
          await input.onSaveContext({ buildingName: value });
          await input.onSavePosition({
            building: value,
            buildingId: input.buildingOptions.find(building => building.name === value)?.id ?? null,
            floorplanBlobId: null,
            floorplanLat: null,
            floorplanLng: null,
            floorplanNotPlacedReason: '',
            floorplanState: '',
          });
        },
      );
    case INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.floorName:
      return nativeSelectField(
        input.context.floorName || input.position.floor,
        fieldWizardText('context.floorName', 'description', 'Confirm the floor or outside/not specified value.'),
        fieldWizardText('context.floorName', 'placeholder', 'Select floor'),
        getDefaultBuildingFloorOptions().map(option => ({ label: option.label, value: option.value })),
        async value => {
          await input.onSaveContext({ floorName: value });
          await input.onSavePosition({
            floor: value,
            floorplanBlobId: null,
            floorplanLat: null,
            floorplanLng: null,
            floorplanNotPlacedReason: '',
            floorplanState: '',
          });
        },
      );
    case INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.ownerName:
      return createOwnerNameFieldDefinition(input);
    case INVENTORIZE_CONTEXT_REVIEW_FIELD_IDS.tenantName:
      return catalogField(
        input.context.tenantName,
        fieldWizardText('context.tenantName', 'description', 'Confirm the tenant if it is known.'),
        fieldWizardText('context.tenantName', 'placeholder', 'Tenant'),
        input.tenantOptions,
        value => input.onSaveContext({ tenantName: value }),
      );
    default:
      return null;
  }
}

function createIdentityFieldDefinition(input: FieldWizardDefinitionInput): FieldWizardDefinition | null {
  switch (input.field.id) {
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.serial:
      return textField(
        input.identity.serial,
        fieldWizardText('identity.serial', 'description', 'Confirm the serial number parsed from detail photos.'),
        fieldWizardText('identity.serial', 'placeholder', 'Serial'),
        value => input.onSaveIdentity({ serial: value }),
        OCR_CANDIDATE_FIELDS.serial,
      );
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.apiKey:
      return {
        ...textField(
          input.identity.apiKey,
          fieldWizardText('identity.apiKey', 'description', 'Confirm the QR/API key.'),
          fieldWizardText('identity.apiKey', 'placeholder', 'API key'),
          value => input.onSaveIdentity({ apiKey: value }),
        ),
        scanPurpose: VALUE_SCAN_PURPOSES.apiKey,
      };
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.placement:
      return optionField(
        input.identity.placement,
        fieldWizardText('identity.placement', 'description', 'Choose whether this meter is main or a submeter.'),
        fieldWizardText('identity.placement', 'placeholder', 'Select placement'),
        getInventorizationPlacementOptions(),
        value => input.onSaveIdentity({ placement: readMeterPlacementSelection(value) }),
      );
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.newMeter:
      return {
        ...optionField(
          formatInventorizationNewMeterSelection(input.identity.newMeter),
          fieldWizardText(
            'identity.newMeter',
            'description',
            'Mark this as a new submeter suggestion when the physical meter identity is not known yet.',
          ),
          fieldWizardText('identity.newMeter', 'placeholder', 'Select state'),
          getInventorizationNewMeterOptions(),
          value => input.onSaveIdentity({ newMeter: readInventorizationNewMeterSelection(value) }),
        ),
        type: 'button-grid',
      };
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.commodity:
      return {
        ...optionField(
          input.identity.commodity,
          fieldWizardText('identity.commodity', 'description', 'Choose what this meter measures.'),
          fieldWizardText('identity.commodity', 'placeholder', 'Select commodity'),
          getInventorizationCommodityOptions(),
          async value => {
            await input.onSaveIdentity({ commodity: readCommodity(value) });

            for (const register of input.registers) {
              await input.onSaveRegisterReading(register.id, {
                obis: '',
                tariff: '',
                unit: '',
              });
            }
          },
        ),
        type: 'button-grid',
      };
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.manufacturer:
      return catalogField(
        input.identity.manufacturer,
        fieldWizardText(
          'identity.manufacturer',
          'description',
          'Choose or confirm the manufacturer from the prefetched model catalog.',
        ),
        fieldWizardText('identity.manufacturer', 'placeholder', 'Search manufacturer'),
        input.manufacturerOptions,
        value => input.onSaveIdentity({ manufacturer: value, meterModel: '' }),
      );
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.meterModel:
      return catalogField(
        input.identity.meterModel,
        fieldWizardText(
          'identity.meterModel',
          'description',
          'Choose or confirm the model for the selected manufacturer.',
        ),
        fieldWizardText('identity.meterModel', 'placeholder', 'Search meter model'),
        input.modelOptions,
        async value => {
          await input.onSaveIdentity({ meterModel: value });

          const selectedModel = input.meterModels.find(model => model.id === value || model.name === value);

          if (selectedModel) {
            await input.onSaveTechnical({
              outputs: mergeInbuiltOutputs(input.technical.outputs, selectedModel.inbuiltOutputs),
            });
          }
        },
      );
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.manufactured:
      return {
        ...textField(
          input.identity.manufactured,
          fieldWizardText('identity.manufactured', 'description', 'Confirm the manufactured year.'),
          fieldWizardText('identity.manufactured', 'placeholder', 'YYYY'),
          value => input.onSaveIdentity({ manufactured: value }),
        ),
        type: 'number',
      };
    case INVENTORIZE_IDENTITY_REVIEW_FIELD_IDS.calibrated:
      return {
        ...textField(
          input.identity.calibrated,
          fieldWizardText('identity.calibrated', 'description', 'Confirm the calibrated year.'),
          fieldWizardText('identity.calibrated', 'placeholder', 'YYYY'),
          value => input.onSaveIdentity({ calibrated: value }),
        ),
        type: 'number',
      };
    default:
      return null;
  }
}

function createReadingFieldDefinition(input: FieldWizardDefinitionInput): FieldWizardDefinition | undefined {
  switch (input.field.id) {
    case 'reading.counter':
      return {
        ...textField(
          input.reading.counter,
          fieldWizardText('reading.counter', 'description', 'Confirm the main register reading.'),
          fieldWizardText('reading.counter', 'placeholder', 'Counter'),
          value => input.onSaveReading({ counter: value }),
          OCR_CANDIDATE_FIELDS.registerCounter,
        ),
        inputMode: 'decimal',
      };
    case 'reading.unit':
      return optionField(
        input.reading.unit,
        fieldWizardText('reading.unit', 'description', 'Confirm the reading unit.'),
        fieldWizardText('reading.unit', 'placeholder', 'Select or enter unit'),
        getUnitOptionsForCommodity(input.identity.commodity, input.reading.obis),
        value => input.onSaveReading({ unit: value }),
        OCR_CANDIDATE_FIELDS.registerUnit,
      );
    case 'reading.obis':
      return optionField(
        input.reading.obis,
        fieldWizardText('reading.obis', 'description', 'Confirm the OBIS code.'),
        fieldWizardText('reading.obis', 'placeholder', 'Select OBIS'),
        getObisOptionsForCommodity(input.identity.commodity),
        value => input.onSaveReading({ obis: value, tariff: '', unit: '' }),
        OCR_CANDIDATE_FIELDS.registerObis,
      );
    case 'reading.tariff':
      return optionField(
        input.reading.tariff || '0',
        fieldWizardText('reading.tariff', 'description', 'Confirm the tariff.'),
        fieldWizardText('reading.tariff', 'placeholder', 'Select tariff'),
        getTariffOptions(),
        value => input.onSaveReading({ tariff: value }),
      );
    default:
      return undefined;
  }
}

function createRegisterFieldDefinition(input: {
  fieldId: InventorizeRegisterReviewFieldId;
  identity: InventorizationIdentity;
  onSaveRegisterReading: (registerId: string, patch: Partial<InventorizationRegisterReading>) => SaveResult;
  registers: InventorizationRegisterReading[];
}): FieldWizardDefinition {
  const register = input.registers.find(candidate => candidate.id === input.fieldId.registerId) ?? {
    counter: '',
    id: input.fieldId.registerId,
    label: input.fieldId.registerId,
    obis: '',
    photoRef: input.fieldId.registerId,
    tariff: '',
    unit: '',
  };
  const save = (patch: Partial<InventorizationRegisterReading>) =>
    input.onSaveRegisterReading(input.fieldId.registerId, patch);

  switch (input.fieldId.field) {
    case 'label':
      return textField(
        register.label,
        fieldWizardText('register.label', 'description', 'Confirm the register label.'),
        fieldWizardText('register.label', 'placeholder', 'TOT'),
        value => save({ label: value }),
      );
    case 'counter':
      return {
        ...textField(
          register.counter,
          fieldWizardText('register.counter', 'description', 'Confirm the register counter reading.'),
          fieldWizardText('register.counter', 'placeholder', 'Counter'),
          value => save({ counter: value }),
          OCR_CANDIDATE_FIELDS.registerCounter,
        ),
        inputMode: 'decimal',
      };
    case 'unit':
      return optionField(
        register.unit,
        fieldWizardText('register.unit', 'description', 'Confirm the register unit.'),
        fieldWizardText('register.unit', 'placeholder', 'Select or enter unit'),
        getUnitOptionsForCommodity(input.identity.commodity, register.obis),
        value => save({ unit: value }),
        OCR_CANDIDATE_FIELDS.registerUnit,
      );
    case 'obis':
      return optionField(
        register.obis,
        fieldWizardText('register.obis', 'description', 'Confirm the register OBIS code.'),
        fieldWizardText('register.obis', 'placeholder', 'Select OBIS'),
        getObisOptionsForCommodity(input.identity.commodity),
        value => save({ obis: value, tariff: '', unit: '' }),
        OCR_CANDIDATE_FIELDS.registerObis,
      );
    case 'tariff':
      return optionField(
        register.tariff || '0',
        fieldWizardText('register.tariff', 'description', 'Confirm the register tariff.'),
        fieldWizardText('register.tariff', 'placeholder', 'Select tariff'),
        getTariffOptions(),
        value => save({ tariff: value }),
      );
  }
}

function createTechnicalFieldDefinition(input: FieldWizardDefinitionInput): FieldWizardDefinition {
  switch (input.field.id) {
    case INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.monitoredEntity:
      return textField(
        input.technical.monitoredEntity,
        fieldWizardText('technical.monitoredEntity', 'description', 'Confirm what this meter monitors.'),
        fieldWizardText('technical.monitoredEntity', 'placeholder', 'Cold water supply'),
        value => input.onSaveTechnical({ monitoredEntity: value }),
      );
    case INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.accessibility:
      return {
        currentValue: input.technical.accessibility?.join(', ') ?? '',
        description: fieldWizardText(
          'technical.accessibility',
          'description',
          'Accessibility is edited with the multi-select chip wizard.',
        ),
        placeholder: fieldWizardText('technical.accessibility', 'placeholder', 'Open the accessibility wizard.'),
        save: () => undefined,
        type: 'readonly',
      };
    case INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.mountType:
      return optionField(
        input.technical.mountType,
        fieldWizardText(
          'technical.mountType',
          'description',
          'Confirm whether a remote-reading mount or module is installed.',
        ),
        fieldWizardText('technical.mountType', 'placeholder', 'Select mount type'),
        remoteReadingMountTypeOptions,
        value =>
          input.onSaveTechnical(
            value === InventoryMeterMountType.Free
              ? {
                  mountTechnology: '',
                  mountType: readMountType(value),
                  remoteReadingOwner: '',
                  remoteReadingSerial: '',
                }
              : { mountType: readMountType(value) },
          ),
      );
    case INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.mountTechnology:
      return optionField(
        input.technical.mountTechnology,
        fieldWizardText(
          'technical.mountTechnology',
          'description',
          'Confirm the remote reading mount technology if one is present.',
        ),
        fieldWizardText('technical.mountTechnology', 'placeholder', 'Select mount technology'),
        outputTechnologyOptions,
        value => input.onSaveTechnical({ mountTechnology: readOutputTechnology(value) }),
      );
    case INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.remoteReadingOwner:
      return textField(
        input.technical.remoteReadingOwner,
        fieldWizardText(
          'technical.remoteReadingOwner',
          'description',
          'Confirm who owns or reads the remote-reading module.',
        ),
        fieldWizardText('technical.remoteReadingOwner', 'placeholder', 'BMS, Enmon, tenant, distributor'),
        value => input.onSaveTechnical({ remoteReadingOwner: value }),
      );
    case INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.remoteReadingSerial:
      return textField(
        input.technical.remoteReadingSerial,
        fieldWizardText(
          'technical.remoteReadingSerial',
          'description',
          'Confirm the remote reading module serial if visible.',
        ),
        fieldWizardText('technical.remoteReadingSerial', 'placeholder', 'Remote reading serial'),
        value => input.onSaveTechnical({ remoteReadingSerial: value }),
      );
    case INVENTORIZE_TECHNICAL_DETAIL_REVIEW_FIELD_IDS.lorawanRssi:
      return rssiField(
        input.technical.detail.lorawanRssi ?? '',
        fieldWizardText('technical.lorawanRssi', 'description', 'Confirm the LoRaWAN signal strength.'),
        value =>
          input.onSaveTechnical({
            detail: {
              ...input.technical.detail,
              lorawanRssi: value,
            },
          }),
      );
    case INVENTORIZE_TECHNICAL_DETAIL_REVIEW_FIELD_IDS.nbiotRssi:
      return rssiField(
        input.technical.detail.nbiotRssi ?? '',
        fieldWizardText('technical.nbiotRssi', 'description', 'Confirm the NB-IoT signal strength.'),
        value =>
          input.onSaveTechnical({
            detail: {
              ...input.technical.detail,
              nbiotRssi: value,
            },
          }),
      );
    case INVENTORIZE_TECHNICAL_REVIEW_FIELD_IDS.note:
      return {
        ...textField(
          input.technical.note,
          fieldWizardText('technical.note', 'description', 'Add an internal note if useful.'),
          fieldWizardText('technical.note', 'placeholder', 'Internal note'),
          value => input.onSaveTechnical({ note: value }),
        ),
        type: 'textarea',
      };
    default:
      return createTechnicalDetailFieldDefinition(input);
  }
}

function createTechnicalDetailFieldDefinition(input: FieldWizardDefinitionInput): FieldWizardDefinition {
  const key = readInventorizeTechnicalDetailReviewFieldKey(input.field.id);

  if (!key) {
    return {
      currentValue: '',
      description: fieldWizardText(
        'technical.photoEvidence',
        'description',
        'Use the photo cards below for this evidence field.',
      ),
      placeholder: fieldWizardText('technical.photoEvidence', 'placeholder', 'This field is handled by photo capture.'),
      save: () => undefined,
      type: 'readonly',
    };
  }

  if (key === OWNER_TYPE_DETAIL_KEY) {
    return {
      ...optionField(
        input.technical.detail[OWNER_TYPE_DETAIL_KEY] ?? '',
        detailWizardDescription(OWNER_TYPE_DETAIL_KEY),
        detailWizardPlaceholder(OWNER_TYPE_DETAIL_KEY, 'Select value'),
        getDetailEnumOptions(OWNER_TYPE_DETAIL_KEY, input.identity.commodity, input.technical.detail) ?? [],
        async value => {
          await input.onSaveTechnical({
            detail: {
              ...input.technical.detail,
              [OWNER_TYPE_DETAIL_KEY]: value,
            },
          });
          await input.onSaveContext({ ownerName: '' });
        },
      ),
      type: 'button-grid',
    };
  }

  const detailField = createDetailFieldDefinition({
    identity: input.identity,
    key,
    onSaveTechnical: input.onSaveTechnical,
    technical: input.technical,
  });

  if (detailField) {
    return detailField;
  }

  return detailTextField({
    key,
    onSaveTechnical: input.onSaveTechnical,
    technical: input.technical,
  });
}

function createOwnerNameFieldDefinition(input: FieldWizardDefinitionInput): FieldWizardDefinition {
  const description = fieldWizardText('context.ownerName', 'description', 'Confirm the owner if it is known.');
  const placeholder = fieldWizardText('context.ownerName', 'placeholder', 'Owner');
  const save = (value: string) => input.onSaveContext({ ownerName: value });

  if (input.ownerType === MeterOwnerType.UtilityCompany) {
    return catalogField(input.context.ownerName, description, placeholder, input.utilityCompanyOwnerOptions, save);
  }

  return catalogField(input.context.ownerName, description, placeholder, input.ownerSuggestions, save);
}

function createDetailFieldDefinition(input: {
  identity: InventorizationIdentity;
  key: string;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => SaveResult;
  technical: InventorizationTechnicalDetails;
}): FieldWizardDefinition | null {
  const currentValue = input.technical.detail[input.key] ?? '';
  const save = createDetailSave(input);
  const description = detailWizardDescription(input.key);

  if (isBooleanDetailKey(input.key)) {
    return {
      ...optionField(
        currentValue,
        description,
        detailWizardPlaceholder(input.key, 'Select value'),
        [
          {
            label: translateInventoryText('app.common.notSpecified', 'Not specified'),
            value: INVENTORIZATION_BOOLEAN_DETAIL_SELECTIONS.unspecified,
          },
          {
            label: translateInventoryText('app.inventory.review.values.yes', 'Yes'),
            value: INVENTORIZATION_BOOLEAN_DETAIL_SELECTIONS.true,
          },
          {
            label: translateInventoryText('app.inventory.review.values.no', 'No'),
            value: INVENTORIZATION_BOOLEAN_DETAIL_SELECTIONS.false,
          },
        ],
        save,
      ),
      type: 'button-grid',
    };
  }

  const enumOptions = getDetailEnumOptions(input.key, input.identity.commodity, input.technical.detail);

  if (enumOptions) {
    return optionField(
      currentValue,
      description,
      detailWizardPlaceholder(input.key, 'Select value'),
      enumOptions,
      save,
    );
  }

  if (input.key === WIDTH_DETAIL_KEY && input.identity.commodity === Commodity.Electricity) {
    const widthUnit = readWidthUnit(input.technical.detail[WIDTH_UNIT_DETAIL_KEY]);
    const widthValue = input.technical.detail[WIDTH_VALUE_DETAIL_KEY] ?? currentValue;

    return {
      currentValue: formatWidthInputValue(widthValue, widthUnit),
      description,
      inputMode: 'decimal',
      placeholder: detailWizardPlaceholder(input.key, 'Number'),
      save: createWidthDetailSave(input),
      type: 'width',
      widthUnit,
    };
  }

  if (isNumberDetailKey(input.key)) {
    return {
      ...textField(currentValue, description, detailWizardPlaceholder(input.key, 'Number'), save),
      inputMode: 'decimal',
      type: 'number',
    };
  }

  return null;
}

function detailTextField(input: {
  key: string;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => SaveResult;
  technical: InventorizationTechnicalDetails;
}): FieldWizardDefinition {
  return textField(
    input.technical.detail[input.key] ?? '',
    detailWizardDescription(input.key),
    detailWizardLabel(input.key),
    createDetailSave(input),
  );
}

function createDetailSave(input: {
  key: string;
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => SaveResult;
  technical: InventorizationTechnicalDetails;
}) {
  return (value: string) =>
    input.onSaveTechnical({
      detail: {
        ...input.technical.detail,
        [input.key]: value,
      },
    });
}

function createWidthDetailSave(input: {
  onSaveTechnical: (patch: Partial<InventorizationTechnicalDetails>) => SaveResult;
  technical: InventorizationTechnicalDetails;
}) {
  return (value: string) => {
    const width = readWidthInputValue(value, input.technical.detail[WIDTH_UNIT_DETAIL_KEY]);

    return input.onSaveTechnical({
      detail: {
        ...input.technical.detail,
        [WIDTH_UNIT_DETAIL_KEY]: width.unit,
        [WIDTH_VALUE_DETAIL_KEY]: width.value,
      },
    });
  };
}

function isBooleanDetailKey(key: string) {
  return isDetailKeyIn(BOOLEAN_DETAIL_KEYS, key);
}

function isNumberDetailKey(key: string) {
  return isDetailKeyIn(WIZARD_NUMBER_DETAIL_KEYS, key);
}

function isDetailKeyIn(values: readonly string[], key: string) {
  return values.some(value => value === key);
}

function readWidthInputValue(value: string, fallbackUnit: string | undefined) {
  const trimmedValue = value.trim();
  const unitMatch = trimmedValue.match(/\s(mm|DIN)$/i);

  return {
    unit: unitMatch?.[1]?.toUpperCase() === WIDTH_UNIT_DIN ? WIDTH_UNIT_DIN : readWidthUnit(fallbackUnit),
    value: unitMatch ? trimmedValue.slice(0, unitMatch.index).trim() : trimmedValue,
  };
}

function readWidthUnit(value: string | undefined): WidthUnit {
  return value === WIDTH_UNIT_DIN ? WIDTH_UNIT_DIN : WIDTH_UNIT_MM;
}

function formatWidthInputValue(value: string, unit: WidthUnit) {
  const trimmedValue = value.trim();

  return trimmedValue ? `${trimmedValue} ${unit}` : '';
}
